/**
 * SceneManager：渲染管线编排。
 *  大气全屏Pass(HDR) → 亮度降采样链 → Pattanaik 自适应曝光 → 色调映射(屏幕)
 */

import * as THREE from 'three'
import { CameraRig } from './CameraRig'
import { QualityManager } from './Quality'
import type { QualityTier } from './Quality'
import { AtmosphereRenderer } from '../atmosphere/AtmosphereRenderer'
import { TransmittanceLUT } from '../atmosphere/TransmittanceLUT'
import { MultiScatterLUT } from '../atmosphere/MultiScatterLUT'
import type { ScatteringMode } from '../atmosphere/types'
import { AutoExposure } from '../postprocess/AutoExposure'
import { tonemapShader } from '../postprocess/shaders/tonemap.glsl'
import { fullscreenVertexShader } from '../atmosphere/shaders/fullscreen.glsl'
import { computeCoeffs, defaultParams } from '../utils/atmosphereParams'
import type { AtmosphereParams } from '../utils/atmosphereParams'

export interface AppParams extends AtmosphereParams {
  manualExposure: number
  adaptSpeed: number
  scatteringMode: ScatteringMode
  msIntensity: number
  msOrder: number
  msDirCount: number
  rayleighOnly: boolean
  mieOnly: boolean
  noScatter: boolean
  quality: QualityTier
}

export function defaultAppParams(): AppParams {
  return {
    ...defaultParams(),
    manualExposure: 1.0,
    adaptSpeed: 1.2,
    scatteringMode: 'single',
    msIntensity: 1.0,
    msOrder: 2,
    msDirCount: 8,
    rayleighOnly: false,
    mieOnly: false,
    noScatter: false,
    quality: 'medium',
  }
}

interface TweenKeyMap {
  [key: string]: number
}

export class SceneManager {
  readonly params: AppParams
  readonly renderer: THREE.WebGLRenderer
  readonly rig: CameraRig
  readonly quality: QualityManager

  private container: HTMLElement
  private scene = new THREE.Scene()
  private tonemapScene = new THREE.Scene()
  private clock = new THREE.Clock()
  private raf = 0
  private disposed = false

  private atmosphere: AtmosphereRenderer
  private atmosphereRT: THREE.WebGLRenderTarget
  private exposure: AutoExposure
  private tonemapMat: THREE.ShaderMaterial
  private tonemapMesh: THREE.Mesh
  private ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
  private lut: TransmittanceLUT
  private msLUT: MultiScatterLUT
  private msPlaceholder: THREE.DataTexture
  private coeffs = computeCoeffs(defaultParams())

  private hudFps: HTMLElement
  private hudRes: HTMLElement
  private hudQuality: HTMLElement

  private tween: {
    from: TweenKeyMap
    to: TweenKeyMap
    t: number
    dur: number
  } | null = null

  /** 面板回调注册（避免直接依赖 UI 模块） */
  onParamsChanged: (() => void) | null = null

  /** 调试：从大气 RT 采样像素（半精度解码），用于验证渲染结果 */
  debugSample(): Record<string, unknown> {
    const rt = this.atmosphereRT
    const w = rt.width
    const h = rt.height
    const buf = new Uint16Array(w * h * 4)
    this.renderer.readRenderTargetPixels(
      rt,
      0,
      0,
      w,
      h,
      buf as unknown as Uint8Array,
    )
    const at = (x: number, y: number): number[] => {
      const i = (y * w + x) * 4
      return [
        SceneManager.halfToFloat(buf[i]),
        SceneManager.halfToFloat(buf[i + 1]),
        SceneManager.halfToFloat(buf[i + 2]),
      ]
    }
    return {
      w,
      h,
      programs: this.renderer.info.programs?.length ?? 0,
      render: {
        calls: this.renderer.info.render.calls,
        triangles: this.renderer.info.render.triangles,
      },
      camera: {
        pos: this.rig.camera.position.toArray(),
        far: this.rig.camera.far,
        near: this.rig.camera.near,
      },
      samples: {
        top: at(Math.floor(w * 0.5), Math.floor(h * 0.15)),
        mid: at(Math.floor(w * 0.5), Math.floor(h * 0.5)),
        low: at(Math.floor(w * 0.5), Math.floor(h * 0.9)),
        sun: at(Math.floor(w * 0.75), Math.floor(h * 0.35)),
      },
    }
  }

  /** 调试：统计 RT 中非零像素数（全图扫描） */
  debugScan(): Record<string, unknown> {
    const scan = (rt: THREE.WebGLRenderTarget): number => {
      const w = rt.width
      const h = rt.height
      const buf = new Uint16Array(w * h * 4)
      this.renderer.readRenderTargetPixels(
        rt,
        0,
        0,
        w,
        h,
        buf as unknown as Uint8Array,
      )
      let n = 0
      for (let i = 0; i < buf.length; i += 4) {
        if (buf[i] !== 0 || buf[i + 1] !== 0 || buf[i + 2] !== 0) n++
      }
      return n
    }
    return {
      atmosphereNonZero: scan(this.atmosphereRT),
    }
  }

  /** 调试：复刻着色器的球体相交/大气区间逻辑，诊断射线段 */
  debugRay(): Record<string, unknown> {
    const cam = this.rig.camera
    const fwd = new THREE.Vector3()
    cam.getWorldDirection(fwd)
    const rt = this.coeffs.Rt
    const rPlanet = this.coeffs.R
    const oc = cam.position.clone()
    const intersectSphere = (
      ro: THREE.Vector3,
      rd: THREE.Vector3,
      radius: number,
    ): [number, number] | null => {
      const b = ro.dot(rd)
      const c = ro.lengthSq() - radius * radius
      const disc = b * b - c
      if (disc < 0) return null
      const sq = Math.sqrt(disc)
      const t0 = -b - sq
      const t1 = -b + sq
      if (t1 < 0) return null
      return [Math.max(t0, 0), t1]
    }
    const top = intersectSphere(oc, fwd, rt)
    let seg: [number, number] | null = null
    if (top) {
      const entry = Math.max(top[0], 0)
      let exit = top[1]
      const ground = intersectSphere(oc, fwd, rPlanet)
      if (ground && ground[0] > entry) exit = Math.min(exit, ground[0])
      if (exit > entry) seg = [entry, exit]
    }
    return {
      camPos: oc.toArray(),
      radius: oc.length(),
      rTop: rt,
      rPlanet,
      fwd: fwd.toArray(),
      top,
      seg,
    }
  }

  private static halfToFloat(h: number): number {
    const s = (h & 0x8000) >> 15
    const e = (h & 0x7c00) >> 10
    const m = h & 0x03ff
    if (e === 0) return (s ? -1 : 1) * Math.pow(2, -14) * (m / 1024)
    if (e === 0x1f) return m ? NaN : s ? -Infinity : Infinity
    return (s ? -1 : 1) * Math.pow(2, e - 15) * (1 + m / 1024)
  }

  /** 调试：粗网格采样 atmosphereRT（半精度解码） */
  debugGrid(cols = 6, rows = 4): Record<string, unknown> {
    const read = (rt: THREE.WebGLRenderTarget): number[][][] => {
      const w = rt.width
      const h = rt.height
      const buf = new Uint16Array(w * h * 4)
      this.renderer.readRenderTargetPixels(
        rt,
        0,
        0,
        w,
        h,
        buf as unknown as Uint8Array,
      )
      const grid: number[][][] = []
      for (let r = 0; r < rows; r++) {
        const y = Math.floor((h * (r + 0.5)) / rows)
        const row: number[][] = []
        for (let c = 0; c < cols; c++) {
          const x = Math.floor((w * (c + 0.5)) / cols)
          const i = (y * w + x) * 4
          row.push([
            SceneManager.halfToFloat(buf[i]),
            SceneManager.halfToFloat(buf[i + 1]),
            SceneManager.halfToFloat(buf[i + 2]),
          ])
        }
        grid.push(row)
      }
      return grid
    }
    return {
      atmosphere: read(this.atmosphereRT),
    }
  }

  /** 调试：采样多次散射 LUT 关键像素（半精度解码），验证预计算结果 */
  debugSampleMSLUT(): Record<string, unknown> {
    const rt = this.msLUT.texture
    const w = rt.width
    const h = rt.height
    const buf = new Uint16Array(w * h * 4)
    this.renderer.readRenderTargetPixels(
      rt,
      0,
      0,
      w,
      h,
      buf as unknown as Uint8Array,
    )
    const at = (fx: number, fy: number): number[] => {
      const x = Math.min(w - 1, Math.max(0, Math.floor(fx * w)))
      const y = Math.min(h - 1, Math.max(0, Math.floor(fy * h)))
      const i = (y * w + x) * 4
      return [
        SceneManager.halfToFloat(buf[i]),
        SceneManager.halfToFloat(buf[i + 1]),
        SceneManager.halfToFloat(buf[i + 2]),
      ]
    }
    return {
      w,
      h,
      // (x=V·L, y=L·up)：采样逆光、天顶太阳、地平线、太阳下方等关键点
      backlight: at(0.05, 0.9), // 逆光视角，太阳高挂
      forward: at(0.95, 0.9), // 顺光视角，太阳高挂
      sunBelow: at(0.5, 0.1), // 太阳低于地平线（多次散射应仍非零）
      zenithSun: at(0.5, 0.98), // 太阳近天顶
      horizon: at(0.5, 0.5), // 地平线、太阳地平线
    }
  }

  constructor(container: HTMLElement, canvas: HTMLCanvasElement) {
    this.container = container
    this.params = defaultAppParams()
    this.quality = new QualityManager()
    this.params.quality = this.quality.tier

    // ---------- 渲染器 ----------
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    })
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace
    this.renderer.toneMapping = THREE.NoToneMapping
    this.renderer.info.autoReset = false // 由 loop 每帧手动重置，便于调试统计

    // ---------- 相机 ----------
    const camera = new THREE.PerspectiveCamera(50, 1, 0.001, 4000)
    this.rig = new CameraRig(camera, canvas)
    this.scene.add(camera)

    // ---------- 目标 RT ----------
    this.atmosphereRT = SceneManager.makeHDRTarget(1, 1)

    // ---------- 大气 ----------
    this.lut = new TransmittanceLUT(this.coeffs)
    this.atmosphere = new AtmosphereRenderer()

    // ---------- 多次散射 LUT ----------
    this.msPlaceholder = new THREE.DataTexture(
      new Uint8Array([0, 0, 0, 255]),
      1,
      1,
    )
    this.msPlaceholder.needsUpdate = true
    this.msLUT = new MultiScatterLUT()

    // ---------- 曝光 ----------
    this.exposure = new AutoExposure(this.renderer)

    // ---------- 色调映射 ----------
    this.tonemapMat = new THREE.ShaderMaterial({
      vertexShader: fullscreenVertexShader,
      fragmentShader: tonemapShader,
      uniforms: {
        uScene: { value: this.atmosphereRT.texture },
        uAdapt: { value: this.exposure.exposureTexture },
        uManualExposure: { value: this.params.manualExposure },
      },
      depthTest: false,
      depthWrite: false,
    })
    this.tonemapMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2),
      this.tonemapMat,
    )
    this.tonemapMesh.frustumCulled = false
    this.tonemapScene.add(this.tonemapMesh)

    // ---------- HUD ----------
    this.hudFps = document.getElementById('hud-fps')!
    this.hudRes = document.getElementById('hud-res')!
    this.hudQuality = document.getElementById('hud-quality')!

    this.applyParams()

    window.addEventListener('resize', this.onResize)
    this.applySize()
    this.loop()
  }

  private static makeHDRTarget(w: number, h: number): THREE.WebGLRenderTarget {
    return new THREE.WebGLRenderTarget(Math.max(1, w), Math.max(1, h), {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: false,
    })
  }

  /** 仅刷新大气物理相关 uniform 与 LUT（tween 过程中逐帧调用） */
  private refreshAtmosphere(): void {
    this.coeffs = computeCoeffs(this.params)
    this.lut.update(this.coeffs)
    this.atmosphere.setCoeffs(this.coeffs, this.lut.texture)
    this.msLUT.setCoeffs(this.coeffs, this.lut.texture)
    this.atmosphere.setParams({
      sampleSteps: this.quality.sampleSteps,
      scatteringMode: this.params.scatteringMode,
      msIntensity: this.params.msIntensity,
      msDirCount: this.params.msDirCount,
      rayleighOnly: this.params.rayleighOnly,
      mieOnly: this.params.mieOnly,
      noScatter: this.params.noScatter,
    })
    this.tonemapMat.uniforms.uManualExposure.value = this.params.manualExposure
  }

  /** 由 params + quality 应用全部着色器/相机/尺寸状态 */
  applyParams(): void {
    this.refreshAtmosphere()
    this.rig.setState({ altitudeKm: this.params.altitudeKm })
    this.applySize()
    this.onParamsChanged?.()
  }

  private onResize = (): void => {
    this.applySize()
  }

  /** 根据容器尺寸 / DPR / 分辨率缩放重建所有目标 */
  applySize(): void {
    const cw = this.container.clientWidth || 1
    const ch = this.container.clientHeight || 1
    const dpr = Math.min(window.devicePixelRatio || 1, this.quality.dprCap)
    const scale = this.quality.resolutionScale

    this.renderer.setPixelRatio(dpr)
    this.renderer.setSize(cw * scale, ch * scale, false)
    this.rig.camera.aspect = cw / ch
    this.rig.camera.updateProjectionMatrix()

    const w = Math.max(1, Math.floor(cw * scale * dpr))
    const h = Math.max(1, Math.floor(ch * scale * dpr))
    this.atmosphereRT.setSize(w, h)
    this.exposure.resize(w, h)
  }

  /** 面板设置质量档 */
  setQuality(tier: QualityTier): void {
    this.params.quality = tier
    this.quality.setTier(tier)
    this.atmosphere.setParams({
      sampleSteps: this.quality.sampleSteps,
      scatteringMode: this.params.scatteringMode,
      msIntensity: this.params.msIntensity,      msDirCount: this.params.msDirCount,      rayleighOnly: this.params.rayleighOnly,
      mieOnly: this.params.mieOnly,
      noScatter: this.params.noScatter,
    })
    this.applySize()
    this.onParamsChanged?.()
  }

  /** 预设场景（带 tween 过渡） */
  applyPreset(name: string): void {
    const P = PRESETS[name]
    if (!P) return
    const from: TweenKeyMap = {
      sunElevationDeg: this.params.sunElevationDeg,
      sunAzimuthDeg: this.params.sunAzimuthDeg,
      turbidity: this.params.turbidity,
      aerosolDensity: this.params.aerosolDensity,
      altitudeKm: this.params.altitudeKm,
      yaw: this.rig.yaw,
      pitch: this.rig.pitch,
    }
    const to: TweenKeyMap = {
      sunElevationDeg: P.sunElevationDeg,
      sunAzimuthDeg: P.sunAzimuthDeg ?? this.params.sunAzimuthDeg,
      turbidity: P.turbidity,
      aerosolDensity: P.aerosolDensity,
      altitudeKm: P.altitudeKm,
      yaw: P.yaw ?? this.rig.yaw,
      pitch: P.pitch ?? this.rig.pitch,
    }
    this.tween = { from, to, t: 0, dur: 1.4 }
  }

  private updateTween(dt: number): void {
    if (!this.tween) return
    this.tween.t += dt
    const k = Math.min(1, this.tween.t / this.tween.dur)
    const s = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2
    const keys = Object.keys(this.tween.to)
    for (const key of keys) {
      const from = this.tween.from[key]
      const to = this.tween.to[key]
      const v = from + (to - from) * s
      if (key === 'yaw' || key === 'pitch') {
        ;(this.rig as unknown as Record<string, number>)[key] = v
      } else if (key === 'altitudeKm') {
        this.params.altitudeKm = v
        this.rig.altitudeKm = v
      } else {
        ;(this.params as unknown as Record<string, number>)[key] = v
      }
    }
    if (k >= 1) {
      this.tween = null
      this.applyParams()
    } else {
      this.refreshAtmosphere()
    }
  }

  private loop = (): void => {
    if (this.disposed) return
    this.raf = requestAnimationFrame(this.loop)

    this.renderer.info.reset()
    const dt = Math.min(this.clock.getDelta(), 0.1)
    this.rig.update()
    this.updateTween(dt)
    // 拖动/缩放海拔后回写参数（供面板显示）
    this.params.altitudeKm = this.rig.altitudeKm
    this.quality.update(dt)

    // 相机基
    const cam = this.rig.camera
    const fwd = new THREE.Vector3()
    cam.getWorldDirection(fwd)
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(cam.quaternion)
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(cam.quaternion)
    const aspect = cam.aspect
    const tanHalf = new THREE.Vector2(
      Math.tan((cam.fov * Math.PI) / 360) * aspect,
      Math.tan((cam.fov * Math.PI) / 360),
    )
    this.atmosphere.setCameraBasis(cam.position, right, up, fwd, tanHalf)

    // 0) 多次散射 LUT pass（仅 precomputedLUT 模式需要）
    if (this.params.scatteringMode === 'precomputedLUT') {
      this.msLUT.render(this.renderer)
      this.atmosphere.setMSLUT(this.msLUT.texture.texture)
    } else {
      this.atmosphere.setMSLUT(this.msPlaceholder)
    }

    // 1) 大气 → atmosphereRT
    const size = new THREE.Vector2()
    this.renderer.getDrawingBufferSize(size)
    this.atmosphere.render(this.renderer, this.atmosphereRT, size)

    // 2) 曝光
    this.exposure.update(dt, this.params.adaptSpeed, this.atmosphereRT.texture)
    this.tonemapMat.uniforms.uAdapt.value = this.exposure.exposureTexture

    // 3) 色调映射 → 屏幕
    this.renderer.setRenderTarget(null)
    this.renderer.render(this.tonemapScene, this.ortho)

    // HUD
    this.hudFps.textContent = `${Math.round(this.quality.fps)} fps`
    this.hudRes.textContent = `${size.x}×${size.y}`
    this.hudQuality.textContent = this.quality.tier
    if (this.tween) this.onParamsChanged?.()
  }

  dispose(): void {
    this.disposed = true
    cancelAnimationFrame(this.raf)
    window.removeEventListener('resize', this.onResize)
    this.rig.dispose()
    this.atmosphereRT.dispose()
    this.atmosphere.dispose()
    this.exposure.dispose()
    this.msLUT.dispose()
    this.msPlaceholder.dispose()
    this.tonemapMat.dispose()
  }
}

interface Preset {
  sunElevationDeg: number
  sunAzimuthDeg?: number
  turbidity: number
  aerosolDensity: number
  altitudeKm: number
  yaw?: number
  pitch?: number
}

const PRESETS: Record<string, Preset> = {
  白天: {
    sunElevationDeg: 60,
    turbidity: 1.0,
    aerosolDensity: 0.2,
    altitudeKm: 0.002,
    pitch: 0.5,
  },
  黄昏: {
    sunElevationDeg: 2,
    turbidity: 2.5,
    aerosolDensity: 0.6,
    altitudeKm: 0.002,
    pitch: 0.5,
  },
  夜晚: {
    sunElevationDeg: -20,
    turbidity: 1.0,
    aerosolDensity: 0.1,
    altitudeKm: 0.002,
    pitch: 0.5,
  },
  太空: {
    sunElevationDeg: 30,
    turbidity: 1.0,
    aerosolDensity: 0.2,
    altitudeKm: 200,
    pitch: -0.6,
  },
}
